import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Button, Popover, Tooltip } from '@mui/material';
import classNames from 'classnames';
import {
    selectCompanySettings,
    selectOperationalStatusesSortedByCode,
} from 'modules/settings/stores/company-settings-slice-handler';
import { selectUsersListFilters, setContactsFilters } from '../stores/contacts-slice-handler';
import { AVAILABILITY, CONNECTIVITY } from 'constants/user-settings';
import LabelWithCheckbox from 'modules/shared/components/label-with-checkbox';
import t from 'utils/translate';

/**
 * Render the contacts filter.
 * @param {boolean} buttonDisabled Disables or enables the button.
 * @return {JSX.Element}
 * @constructor
 */
const ContactsFilter = ({ buttonDisabled }) => {
    const companySettings = useSelector(selectCompanySettings);
    const sortedOperationalStatuses = useSelector(selectOperationalStatusesSortedByCode);
    const usersListFilters = useSelector(selectUsersListFilters);
    const [filters, setFilters] = useState(usersListFilters);
    const [anchorEl, setAnchorEl] = useState(null);

    const defaultOperationalIdsFilter = useMemo(
        () =>
            sortedOperationalStatuses.reduce(
                (acc, currentItem) => (!!currentItem.DefaultFilter ? [...acc, currentItem.OperationalStatusId] : acc),
                [],
            ),
        [sortedOperationalStatuses],
    );

    const handleFilterIconClick = (e) => {
        if (buttonDisabled) {
            return;
        }

        setAnchorEl(e.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
        setFilters(usersListFilters);
    };
    const open = Boolean(anchorEl);
    const displayDefaultBtn = () => {
        if (companySettings.DefaultFilterForAllUsers && sortedOperationalStatuses.length > 0) {
            if (filters.OperationalStatusIds.every((val) => defaultOperationalIdsFilter.includes(val))) {
                return filters.OperationalStatusIds.length !== defaultOperationalIdsFilter.length;
            }
            return true;
        }
        return false;
    };

    const handleFilterSelectionChange = (category, value) => {
        let updatedFilters = {
            ...filters,
        };

        if (category === 'OperationalStatusIds' && value === 'all') {
            updatedFilters[category] =
                updatedFilters[category].length !== sortedOperationalStatuses.length
                    ? sortedOperationalStatuses.map((status) => status.OperationalStatusId)
                    : [];
        } else {
            updatedFilters[category] =
                filters[category].find((v) => v === value) !== undefined
                    ? filters[category].filter((v) => v !== value)
                    : [...filters[category], value];
        }

        setFilters(updatedFilters);
    };

    const setDefaultFilters = () => {
        setContactsFilters({
            ...filters,
            OperationalStatusIds: defaultOperationalIdsFilter,
        });
    };

    const resetFilters = () => {
        const emptyFilters = {
            Connectivity: [],
            UserAvailability: [],
            OperationalStatusIds: [],
        };

        setContactsFilters(emptyFilters);
        handleClose();
    };

    useEffect(() => {
        setFilters(usersListFilters);
    }, [usersListFilters]);

    return (
        <div className="wcf-ui-contacts-filter-container">
            <Tooltip title={t('global_filter')}>
                <div
                    className={'contacts-filter-btn'}
                    onClick={handleFilterIconClick}
                    data-test-id="wcf-ui-contacts-filter-btn"
                >
                    <span
                        className={classNames({
                            'stw-filter': true,
                            'wcf-ui-disabled-control': buttonDisabled || anchorEl,
                            'active-filters':
                                usersListFilters.Connectivity.length ||
                                usersListFilters.UserAvailability.length ||
                                usersListFilters.OperationalStatusIds.length,
                        })}
                    ></span>
                </div>
            </Tooltip>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                className={'wcf-ui-contacts-filters-list'}
                data-test-id="wcf-ui-contacts-filters-list"
            >
                <article className={'filters-list-container'}>
                    <header className={'modal-close'}>
                        <span className="stw-add" data-test-id="contacts-filter-close" onClick={handleClose} />
                    </header>

                    <div className={'filter-sections'}>
                        <section>
                            <header>{t('js_all_contacts_filter_connectivity')}</header>
                            {Object.keys(CONNECTIVITY).map((status) => (
                                <div
                                    className={'wcf-ui-checkbox-container'}
                                    data-test-id={`contacts-filter-${status}`}
                                    key={`connectivity-${status}`}
                                >
                                    <LabelWithCheckbox
                                        labelContent={
                                            <div className={'multi-select-area'}>
                                                {t(`js_all_user_availability_${status.toLowerCase()}`)}
                                            </div>
                                        }
                                        onChange={() => {
                                            handleFilterSelectionChange('Connectivity', CONNECTIVITY[status]);
                                        }}
                                        checked={filters.Connectivity.includes(CONNECTIVITY[status])}
                                    ></LabelWithCheckbox>
                                </div>
                            ))}
                        </section>
                        {companySettings.AllowUserAvailability && (
                            <section>
                                <header>{t('js_all_contacts_filter_user_status')}</header>
                                {Object.keys(AVAILABILITY).map((status) => (
                                    <div
                                        className={'wcf-ui-checkbox-container'}
                                        data-test-id={`contacts-filter-${AVAILABILITY[status]}`}
                                        key={`user-availability-${AVAILABILITY[status]}`}
                                    >
                                        <LabelWithCheckbox
                                            labelContent={
                                                <div className={'multi-select-area'}>
                                                    {t(`js_all_user_availability_${AVAILABILITY[status]}`)}
                                                </div>
                                            }
                                            onChange={() => {
                                                handleFilterSelectionChange('UserAvailability', AVAILABILITY[status]);
                                            }}
                                            checked={filters.UserAvailability.includes(AVAILABILITY[status])}
                                        ></LabelWithCheckbox>
                                    </div>
                                ))}
                            </section>
                        )}
                        {sortedOperationalStatuses.length > 0 && (
                            <section>
                                <header>{t('js_all_contacts_filter_operational_status')}</header>

                                <div className={'operational-statuses-list'}>
                                    <div className={'wcf-ui-checkbox-container'}>
                                        <LabelWithCheckbox
                                            labelContent={
                                                <div className={'multi-select-area'}>
                                                    {t('js_all_contacts_filter_all_options')}
                                                </div>
                                            }
                                            onChange={() => {
                                                handleFilterSelectionChange('OperationalStatusIds', 'all');
                                            }}
                                            checked={
                                                filters.OperationalStatusIds.length === sortedOperationalStatuses.length
                                            }
                                        ></LabelWithCheckbox>
                                    </div>
                                    {sortedOperationalStatuses.map((status) => (
                                        <div
                                            className="wcf-ui-checkbox-container"
                                            key={`operational-status-${status.OperationalStatusId}`}
                                            data-test-id={`contacts-filter-opstatus-${status.OperationalStatusId}`}
                                        >
                                            <LabelWithCheckbox
                                                labelContent={
                                                    <div className="multi-select-area">{`${status.Code} - ${status.Label}`}</div>
                                                }
                                                onChange={() => {
                                                    handleFilterSelectionChange(
                                                        'OperationalStatusIds',
                                                        status.OperationalStatusId,
                                                    );
                                                }}
                                                checked={filters.OperationalStatusIds.includes(
                                                    status.OperationalStatusId,
                                                )}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}
                    </div>

                    <footer>
                        <div className={'left-side'}>
                            <Button
                                onClick={resetFilters}
                                variant="contained"
                                role="primary"
                                data-test-id="contacts-filter-reset-button"
                            >
                                {t('js_all_contacts_filter_reset_button')}
                            </Button>
                        </div>
                        <div className={'right-side'}>
                            {displayDefaultBtn() && (
                                <Button
                                    onClick={setDefaultFilters}
                                    variant="contained"
                                    role="primary"
                                    data-test-id="contacts-filter-default-button"
                                >
                                    {t('global_default')}
                                </Button>
                            )}
                            <Button
                                onClick={() => {
                                    setContactsFilters(filters);
                                    handleClose();
                                }}
                                variant="contained"
                                role="primary"
                                data-test-id="contacts-filter-ok-button"
                            >
                                {t('js_all_ok_button')}
                            </Button>
                        </div>
                    </footer>
                </article>
            </Popover>
        </div>
    );
};

export default ContactsFilter;
